import * as React from 'react';
import {
  faBars,
  faRightFromBracket,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import Button, { ButtonProps } from '../../atoms/Button';
import styles from './TabletNavbar.module.scss';
import Icon from '../../atoms/Icon';
import Text from '../../atoms/Text';

export type MenuItem = Omit<ButtonProps, 'variant' | 'onClick'>;
export interface MenuGroup<T extends MenuItem = MenuItem> {
  id: string;
  label: string;
  color?: ButtonProps['color'];
  items: Array<T>;
}

export interface Props<T extends MenuItem = MenuItem>
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'color'> {
  menu: Array<MenuGroup<T>>;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  color?: ButtonProps['color'];
  fixed?: boolean;
  onItemClick?: (item: Omit<T, 'children' | 'icon'>) => void;
  onLogout?: () => void;
}

/**
 * <TabletNavbar> component
 */
export const TabletNavbar = <T extends MenuItem = MenuItem>({
  className,
  style,
  menu,
  children,
  footer,
  color = 'primary',
  fixed = false,
  onItemClick,
  onLogout,
  ...htmlElementProps
}: Props<T>) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside: EventListener = (event) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShow(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const fixedStyle: React.CSSProperties = fixed
    ? {
        position: 'fixed',
        width: '100%',
      }
    : {};

  return (
    <nav
      className={classNames(styles.navbar, className, {
        [`bg-${color}`]: color,
      })}
      style={{ ...style, ...fixedStyle }}
      ref={ref}
      {...htmlElementProps}
    >
      <div className={styles.container} role="menubar">
        <div className={styles['burger-container']}>
          {menu.length > 0 && (
            <Button
              className={classNames(styles['burger-button'], 'mr-5')}
              variant="link"
              onClick={() => setShow(!show)}
            >
              <Icon color="backgroundColor" icon={!show ? faBars : faTimes} />
            </Button>
          )}
        </div>
        <div className={styles['children-container']}>{children}</div>
      </div>

      <div className={classNames(styles.menu, { show })} role="menu">
        <div className="d-flex flex-column flex-justify-between h-100">
          <div>
            {menu.map((group) => (
              <div key={group.id} className="mb-4">
                <div>
                  <Text
                    color={group.color || 'primary'}
                    weight="bold"
                    size="sm1"
                    isUpperCase
                  >
                    {group.label}
                  </Text>
                </div>
                {group.items.map(
                  ({ icon, children: groupChildren, ...item }) => (
                    <Button
                      id={item.id}
                      key={item.id}
                      role="menuitem"
                      icon={icon}
                      onClick={() => {
                        onItemClick?.(item);
                        setShow(false);
                      }}
                      color={color}
                      variant="link"
                    >
                      {groupChildren}
                    </Button>
                  ),
                )}
              </div>
            ))}
          </div>

          <div className="d-flex flex-column gap-3">
            {onLogout && (
              <Button
                role="menuitem"
                key="btn-sair"
                aria-label="Sair"
                icon={faRightFromBracket}
                onClick={() => onLogout()}
                variant="link"
              >
                Sair
              </Button>
            )}
            {footer}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TabletNavbar;
